import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface MemorySnapshot {
  timestamp: Date;
  usedJSHeapSizeMB: number;
  totalJSHeapSizeMB: number;
  jsHeapSizeLimitMB: number;
  usagePercent: number;
}

export interface MemoryAlert {
  level: 'warning' | 'critical';
  snapshot: MemorySnapshot;
  message: string;
}

/**
 * MemoryWatchdogService — Monitors JS heap usage every minute.
 *
 * - Emits warnings at 70% heap usage
 * - Triggers soft reload at 85% heap usage
 * - Detects consistent memory growth trends (leak indicator)
 * - Stores last 60 snapshots for trend analysis
 *
 * Requires: performance.memory API (Chromium-based WebView / Chrome)
 */
@Injectable({ providedIn: 'root' })
export class MemoryWatchdogService implements OnDestroy {
  /** Stream of latest memory snapshot */
  readonly snapshot$ = new BehaviorSubject<MemorySnapshot | null>(null);

  /** Stream of memory alerts (warning / critical) */
  readonly alert$ = new Subject<MemoryAlert>();

  /** Rolling history of snapshots (max 60) */
  readonly history: MemorySnapshot[] = [];

  private watchIntervalId: ReturnType<typeof setInterval> | null = null;
  private softReloadCallback: (() => void) | null = null;

  private readonly MAX_HISTORY = 60;
  private readonly WARNING_THRESHOLD = 0.70;   // 70%
  private readonly CRITICAL_THRESHOLD = 0.85;  // 85% → auto soft-reload
  private readonly ALERT_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes between alerts
  private lastAlertTime = 0;

  constructor(private readonly ngZone: NgZone) {}

  /**
   * Start the memory watchdog.
   * @param intervalMs How often to sample memory (default: 60s)
   * @param onCritical Optional callback when critical threshold is crossed
   */
  start(intervalMs = 60_000, onCritical?: () => void): void {
    if (!this.isSupported()) {
      console.warn('[MemoryWatchdog] performance.memory is not available in this runtime');
      return;
    }

    this.softReloadCallback = onCritical ?? null;

    this.ngZone.runOutsideAngular(() => {
      // Take an immediate snapshot on start
      this.checkMemory();
      this.watchIntervalId = setInterval(() => this.checkMemory(), intervalMs);
    });

    console.log(`[MemoryWatchdog] Started — sampling every ${intervalMs / 1000}s`);
  }

  stop(): void {
    if (this.watchIntervalId !== null) {
      clearInterval(this.watchIntervalId);
      this.watchIntervalId = null;
    }
  }

  /** Get a formatted memory status string */
  getReport(): string {
    const snap = this.snapshot$.value;
    if (!snap) return 'Memory API not available';
    return (
      `Heap: ${snap.usedJSHeapSizeMB}MB / ${snap.jsHeapSizeLimitMB}MB ` +
      `(${(snap.usagePercent * 100).toFixed(1)}%) — ` +
      `${this.history.length} snapshots recorded`
    );
  }

  /** Returns true if the performance.memory API is available */
  isSupported(): boolean {
    return typeof (performance as any).memory !== 'undefined';
  }

  private checkMemory(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mem = (performance as any).memory as {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };

    if (!mem) return;

    const MB = 1_048_576;
    const snapshot: MemorySnapshot = {
      timestamp: new Date(),
      usedJSHeapSizeMB: Math.round(mem.usedJSHeapSize / MB),
      totalJSHeapSizeMB: Math.round(mem.totalJSHeapSize / MB),
      jsHeapSizeLimitMB: Math.round(mem.jsHeapSizeLimit / MB),
      usagePercent: mem.usedJSHeapSize / mem.jsHeapSizeLimit,
    };

    this.addToHistory(snapshot);

    this.ngZone.run(() => {
      this.snapshot$.next(snapshot);
      this.evaluateThresholds(snapshot);
      this.detectLeakTrend();
    });
  }

  private evaluateThresholds(snapshot: MemorySnapshot): void {
    const now = Date.now();
    const cooldownPassed = now - this.lastAlertTime > this.ALERT_COOLDOWN_MS;
    if (!cooldownPassed) return;

    if (snapshot.usagePercent >= this.CRITICAL_THRESHOLD) {
      this.lastAlertTime = now;
      const alert: MemoryAlert = {
        level: 'critical',
        snapshot,
        message:
          `CRITICAL: ${snapshot.usedJSHeapSizeMB}MB / ${snapshot.jsHeapSizeLimitMB}MB ` +
          `(${(snapshot.usagePercent * 100).toFixed(1)}%) — triggering soft reload`,
      };
      this.alert$.next(alert);
      console.error(`[MemoryWatchdog] ${alert.message}`);
      this.softReloadCallback?.();

    } else if (snapshot.usagePercent >= this.WARNING_THRESHOLD) {
      this.lastAlertTime = now;
      const alert: MemoryAlert = {
        level: 'warning',
        snapshot,
        message:
          `WARNING: ${snapshot.usedJSHeapSizeMB}MB / ${snapshot.jsHeapSizeLimitMB}MB ` +
          `(${(snapshot.usagePercent * 100).toFixed(1)}%)`,
      };
      this.alert$.next(alert);
      console.warn(`[MemoryWatchdog] ${alert.message}`);
    }
  }

  /**
   * Detect sustained memory growth over the last 4 snapshots.
   * Each snapshot must be > 5% larger than the previous.
   */
  private detectLeakTrend(): void {
    if (this.history.length < 4) return;
    const recent = this.history.slice(-4);
    const isGrowing = recent.every((snap, i) => {
      if (i === 0) return true;
      return snap.usedJSHeapSizeMB > recent[i - 1].usedJSHeapSizeMB * 1.05;
    });

    if (isGrowing) {
      console.warn('[MemoryWatchdog] Leak trend detected — sustained heap growth across last 4 samples');
    }
  }

  private addToHistory(snapshot: MemorySnapshot): void {
    this.history.push(snapshot);
    if (this.history.length > this.MAX_HISTORY) {
      this.history.shift();
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.snapshot$.complete();
    this.alert$.complete();
  }
}
